import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import EnhancedSEO from "@/components/SEO/EnhancedSEO";
import premiumGardenLandscaping from "@/assets/premium-garden-landscaping.jpg";
import { 
  Search, 
  MapPin, 
  Star, 
  TrendingUp, 
  Globe, 
  Users, 
  CheckCircle,
  Target,
  BarChart3,
  Smartphone
} from "lucide-react";

const SEOLocalSearch = () => {
  const seoServices = [
    {
      icon: Search,
      title: "Local SEO Optimization",
      description: "Dominate local search results for service-based businesses in your area",
      features: [
        "Google My Business optimization",
        "Local keyword targeting",
        "Location-based content strategy",
        "Local citations & directory listings"
      ]
    },
    {
      icon: MapPin,
      title: "Geographic Targeting",
      description: "Target specific neighborhoods and areas where your ideal clients live",
      features: [
        "Service area optimization",
        "Neighborhood-specific landing pages",
        "Local competitor analysis",
        "Geographic keyword research"
      ]
    },
    {
      icon: Star,
      title: "Review Management",
      description: "Build and maintain a stellar online reputation that attracts premium clients",
      features: [
        "Review generation campaigns",
        "Reputation monitoring",
        "Review response management",
        "5-star review optimization"
      ]
    },
    {
      icon: TrendingUp,
      title: "Technical SEO",
      description: "Ensure your website performs flawlessly in search engines",
      features: [
        "Site speed optimization",
        "Mobile responsiveness",
        "Schema markup implementation",
        "Core Web Vitals optimization"
      ]
    },
    {
      icon: Globe,
      title: "Content Strategy",
      description: "Create content that ranks high and converts visitors into clients",
      features: [
        "Keyword-optimized blog posts",
        "Service page optimization",
        "Local content creation",
        "FAQ optimization"
      ]
    },
    {
      icon: Users,
      title: "Conversion Optimization",
      description: "Turn website visitors into high-value leads and clients",
      features: [
        "Landing page optimization",
        "Call-to-action improvement",
        "Lead magnet creation",
        "Form optimization"
      ]
    }
  ];

  const results = [
    { metric: "150%", label: "Increase in Local Visibility" },
    { metric: "300%", label: "More Qualified Leads" },
    { metric: "4.8/5", label: "Average Client Review Score" },
    { metric: "85%", label: "Increase in Website Traffic" }
  ];

  const benefits = [
    "Appear on the first page of Google for local service searches",
    "Attract clients actively searching for premium professional services",
    "Build trust through consistent 5-star reviews and testimonials",
    "Outrank competitors in your local market",
    "Generate leads 24/7 through organic search traffic",
    "Reduce advertising costs with organic visibility"
  ];

  return (
    <>
      <EnhancedSEO 
        type="seo-services"
        title="Local SEO for Service Businesses | Dominate Your Local Market | VGP"
        description="Comprehensive local SEO services for premium service-based businesses. Get found by high-value local customers searching for quality services. Free SEO audit included."
        keywords="local SEO services, service business SEO, Google Business Profile optimization, local citations, service business marketing"
        url="/seo-local-search"
      />
      <div className="min-h-screen green-theme">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{backgroundImage: `url(${premiumGardenLandscaping})`, filter: 'blur(2px)', transform: 'scale(1.05)'}}></div>
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="container mx-auto px-6 relative z-20">
          <div className="max-w-4xl mx-auto text-center text-white relative z-30">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Dominate Local Search for <span className="text-accent">Service-Based Businesses</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white/90 leading-relaxed">
              Get found by high-value clients searching for premium services in your area. Our proven SEO strategies ensure you appear first when homeowners and business owners need your expertise.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button variant="gold" size="xl" asChild>
                 <a href="/book-call">
                  Get Your SEO Strategy
                </a>
              </Button>
              <Button 
                variant="ghost-gold" 
                size="xl"
                onClick={() => window.open('/seo-case-study', '_blank')}
              >
                View Local SEO Results
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-16 bg-secondary/20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
              Proven Local SEO Results
            </h2>
            <p className="text-xl text-muted-foreground">
              See how our clients dominate local search results
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {results.map((result, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                  {result.metric}
                </div>
                <div className="text-muted-foreground">{result.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
              Complete <span className="text-primary">Local SEO Solutions</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Everything you need to dominate local search results and attract premium service-based clients.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {seoServices.map((service, index) => (
              <Card key={index} className="border-0 shadow-elegant hover:shadow-luxury transition-all duration-300 hover:-translate-y-1 bg-card/90 backdrop-blur-sm">
                <CardHeader className="pb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-primary via-primary to-primary/80 rounded-lg flex items-center justify-center mb-4 shadow-elegant">
                    <service.icon className="w-7 h-7 text-primary-foreground" />
                  </div>
                  <CardTitle className="text-xl text-foreground">{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4 leading-relaxed">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-primary mr-3 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Soft CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary/5 to-secondary/5">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-foreground">
              Ready to Dominate Your Local Market?
            </h3>
            <p className="text-lg text-muted-foreground mb-8">
              Book a free SEO strategy call and discover how we can help you rank #1 for high-value local searches
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button variant="premium" size="lg" asChild>
                <a href="/book-call">
                  📅 Book Free SEO Strategy Call
                </a>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <a href="tel:07450620757">
                  📞 Call Voice Agent: 07450 620757
                </a>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              Free SEO audit included • No obligations • Get ranking today
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-b from-secondary/20 to-background">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                  Why Local SEO is <span className="text-primary">Essential</span> for Service-Based Businesses
                </h2>
                <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                  When customers need professional services, 97% start their search online. 
                  Without strong local SEO, you're invisible to your ideal clients.
                </p>
                
                <div className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start">
                      <CheckCircle className="w-6 h-6 text-primary mr-4 flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-6">
                <Card className="border-0 shadow-elegant bg-card/90 backdrop-blur-sm">
                  <CardContent className="p-6 text-center">
                    <Target className="w-12 h-12 text-primary mx-auto mb-4" />
                    <h3 className="font-bold text-foreground mb-2">Local Targeting</h3>
                    <p className="text-sm text-muted-foreground">Reach clients in specific neighborhoods and service areas</p>
                  </CardContent>
                </Card>
                
                <Card className="border-0 shadow-elegant bg-card/90 backdrop-blur-sm">
                  <CardContent className="p-6 text-center">
                    <Smartphone className="w-12 h-12 text-primary mx-auto mb-4" />
                    <h3 className="font-bold text-foreground mb-2">Mobile Optimization</h3>
                    <p className="text-sm text-muted-foreground">Capture mobile searches from clients on-the-go</p>
                  </CardContent>
                </Card>
                
                <Card className="border-0 shadow-elegant bg-card/90 backdrop-blur-sm">
                  <CardContent className="p-6 text-center">
                    <BarChart3 className="w-12 h-12 text-primary mx-auto mb-4" />
                    <h3 className="font-bold text-foreground mb-2">Performance Tracking</h3>
                    <p className="text-sm text-muted-foreground">Monitor rankings, traffic, and lead generation</p>
                  </CardContent>
                </Card>
                
                <Card className="border-0 shadow-elegant bg-card/90 backdrop-blur-sm">
                  <CardContent className="p-6 text-center">
                    <Star className="w-12 h-12 text-primary mx-auto mb-4" />
                    <h3 className="font-bold text-foreground mb-2">Reputation Building</h3>
                    <p className="text-sm text-muted-foreground">Showcase 5-star reviews and testimonials</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto text-white">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Dominate Local Search?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Get a free local SEO audit and discover how to outrank your competition 
              and attract more premium service-based clients.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button variant="gold" size="xl" asChild>
                <a href="/book-call">
                  Get Free SEO Audit
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default SEOLocalSearch;